import { useEffect, useState } from "react";
import { Accessibility } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE = "kn.a11y.v1";

interface Prefs {
  highContrast: boolean;
  fontPercent: number; // 100 = default
}

function load(): Prefs {
  if (typeof window === "undefined") return { highContrast: false, fontPercent: 100 };
  try {
    return { highContrast: false, fontPercent: 100, ...JSON.parse(localStorage.getItem(STORAGE) ?? "{}") };
  } catch {
    return { highContrast: false, fontPercent: 100 };
  }
}

function apply(p: Prefs) {
  document.documentElement.classList.toggle("high-contrast", p.highContrast);
  document.documentElement.style.setProperty("--user-font-size", `${(p.fontPercent / 100) * 16}px`);
  localStorage.setItem(STORAGE, JSON.stringify(p));
}

export function AccessibilityMenu() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>(load);

  useEffect(() => {
    apply(prefs);
  }, [prefs]);

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="הגדרות נגישות"
        onClick={() => setOpen((v) => !v)}
      >
        <Accessibility className="h-5 w-5" />
      </Button>
      {open && (
        <div
          role="menu"
          aria-label="הגדרות נגישות"
          className="absolute left-0 mt-2 w-64 rounded-md border bg-card p-3 shadow-lg z-40"
        >
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-2">
              <span className="text-sm">ניגודיות גבוהה</span>
              <input
                type="checkbox"
                checked={prefs.highContrast}
                onChange={(e) => setPrefs({ ...prefs, highContrast: e.target.checked })}
                className="h-5 w-5 accent-primary"
              />
            </label>
            <div>
              <div className="text-sm mb-1">גודל טקסט: {prefs.fontPercent}%</div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPrefs({ ...prefs, fontPercent: Math.max(80, prefs.fontPercent - 10) })}
                  aria-label="הקטן טקסט"
                >
                  −
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPrefs({ ...prefs, fontPercent: 100 })}
                >
                  ברירת מחדל
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPrefs({ ...prefs, fontPercent: Math.min(160, prefs.fontPercent + 10) })}
                  aria-label="הגדל טקסט"
                >
                  +
                </Button>
              </div>
            </div>
            <a href="/accessibility" className="block text-sm text-primary hover:underline">
              הצהרת נגישות מלאה
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
